import { type FC } from "react";
import { useParams } from "react-router-dom";
import {
  SpreadsheetEditor,
  type SpreadsheetData,
  type SpreadsheetContextMenuItem,
  SpreadsheetActionType,
} from "@via/components/spreadsheet-editor/spreadsheet-editor.tsx";
import { type InputNode } from "@via/pages/projects/runs/utils/helpers.tsx";
import { type UseFormReturn } from "react-hook-form";
import { createMarkup } from "@via/utils/html-utils.tsx";
import {
  fetchInputFileList,
  fetchVmetaDatasetFiles,
} from "@via/pages/projects/runs/runs.api.tsx";
import { isVmetaCollection } from "@via/pages/projects/runs/overview/inputs/input-file/single-file-spreadsheet";
import { useVmetaDatasetFiles } from "@via/pages/projects/runs/overview/hooks/useVmetaDatasetFiles";
import { buildVmetaDatasetMenuItems } from "@via/pages/projects/runs/overview/utils/vmeta-dataset-menu-items";
import { type SaveRunInputs } from "common";

interface InputSpreadsheetProps {
  form: UseFormReturn;
  inputs: InputNode[];
  editorProcesses: Record<string, any>;
  paramsInputs?: SaveRunInputs[];
}

const InputSpreadsheet: FC<InputSpreadsheetProps> = ({
  form,
  inputs,
  editorProcesses,
  paramsInputs,
}) => {
  const { runId = "" } = useParams();
  const { setValue, watch } = form;
  const { vmetaFilesByCollectionId } = useVmetaDatasetFiles(paramsInputs);

  const getSpreadsheetData = (inputs: InputNode[]): SpreadsheetData => {
    const maxRows = Math.max(
      ...inputs.map(({ id }) => form.getValues(id)?.length ?? 0),
      100 // Fallback to a minimum of 100 rows if no data exists
    );

    return Array.from({ length: maxRows }, (_, rowIndex) =>
      inputs.map(({ id, inputDetails }) => ({
        value: form.getValues(id)?.[rowIndex] ?? "",
        ...(inputDetails?.dropDown && {
          options: inputDetails.dropDown.split(","),
        }),
      }))
    );
  };

  const updateSpreadsheetData =
    (inputs: InputNode[]) => (data: SpreadsheetData) => {
      const transformedData: string[][] = Array.from(
        { length: inputs.length },
        () => []
      );
      // Transform rows into columns to align with `inputs` structure
      data.forEach(row =>
        row.forEach((cell, colIndex) => {
          transformedData[colIndex].push(cell.value as string);
        })
      );
      // Determine the last non-empty row index to trim empty cells
      const lastFilledRow = transformedData.reduce((maxIndex, column) => {
        const lastNonEmptyIndex = column.reduce(
          (lastIndex, value, index) => (value ? index : lastIndex),
          -1
        );
        return Math.max(maxIndex, lastNonEmptyIndex);
      }, -1);
      // Set form values, trimming any extra empty rows
      inputs.forEach(({ id }, colIndex) => {
        setValue(id, transformedData[colIndex].slice(0, lastFilledRow + 1));
      });
    };

  const getSpreadsheetContextMenuItems = (
    inputs: InputNode[]
  ): SpreadsheetContextMenuItem[] => {
    const processKey = inputs[0]?.id.split(".")[0];
    const autofillInputName =
      editorProcesses?.[processKey]?.collection_autofill?.input_name;
    const collectionName = autofillInputName
      ? watch(`params.${autofillInputName}`)
      : undefined;
    const isDatasetVmetaCollection = paramsInputs?.find(
      p => isVmetaCollection(p) && p.vmetaCollectionId
    );
    const vmetaDatasetMenuItems = buildVmetaDatasetMenuItems(
      vmetaFilesByCollectionId
    );

    return [
      ...vmetaDatasetMenuItems,
      ...(autofillInputName && collectionName && !isDatasetVmetaCollection
        ? [
            {
              label: `Insert File Names from "${collectionName}"`,
              type: SpreadsheetActionType.PasteCollectionNames,
              onClick: async () => {
                const selectedInput = Array.isArray(paramsInputs)
                  ? paramsInputs.find(
                      input =>
                        input.name === autofillInputName &&
                        input.value === collectionName
                    )
                  : undefined;

                let autofillCollections: string[] = [];
                if (
                  isVmetaCollection(selectedInput) &&
                  selectedInput.vmetaCollectionId
                ) {
                  const { data: vmetaDatasetFiles } =
                    await fetchVmetaDatasetFiles(
                      selectedInput.vmetaCollectionId
                    );
                  if (Array.isArray(vmetaDatasetFiles)) {
                    autofillCollections = vmetaDatasetFiles.map(
                      file => file.name
                    );
                  }
                } else {
                  const collectionFiles = await fetchInputFileList(runId);

                  autofillCollections = collectionFiles
                    .filter(file =>
                      file?.collectionName?.split(",")?.includes(collectionName)
                    )
                    .map(({ name }) => name);
                }

                return autofillCollections;
              },
            },
          ]
        : []),
    ];
  };

  const renderInputDescription = (info): JSX.Element => (
    <div
      className="mt-1 max-w-[90vw] whitespace-normal text-xs italic text-gray-500"
      dangerouslySetInnerHTML={createMarkup(info)}
    />
  );

  return (
    <div className="mb-5">
      <SpreadsheetEditor
        label={
          inputs.find(({ inputDetails }) => inputDetails?.title)?.inputDetails
            ?.title
        }
        columnLabels={inputs.map(({ name }) => name)}
        data={getSpreadsheetData(inputs)}
        onChange={updateSpreadsheetData(inputs)}
        extraContextMenuItems={getSpreadsheetContextMenuItems(inputs)}
      />
      {inputs
        .filter(({ inputDetails }) => inputDetails?.inDescOpt)
        .map(({ name, inputDetails }, index) => (
          <div key={`${name}_${index}`}>
            {renderInputDescription(`*${name}: ${inputDetails?.inDescOpt}`)}
          </div>
        ))}
    </div>
  );
};

export default InputSpreadsheet;
